/**
 * Helpers for Gmail-style recipient fields: parse pasted "Name <email>" lists,
 * validate addresses, and normalize comma-separated storage for the API.
 */

/** Practical email check (aligned with typical product forms, not full RFC 5322). */
export function isValidEmailAddress(raw: string): boolean {
  const s = raw.trim().toLowerCase();
  if (!s || s.length > 254) return false;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return false;
  const domain = s.split('@')[1];
  if (!domain) return false;
  const lastDot = domain.lastIndexOf('.');
  if (lastDot === -1) return false;
  const tld = domain.slice(lastDot + 1);
  // Final label after the last dot must be at least 2 letters (e.g. .com, .uk), not .c
  return /^[a-z]{2,}$/.test(tld);
}

export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

function dedupeEmails(emails: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of emails) {
    const n = normalizeEmail(e);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

/**
 * Extracts email addresses from pasted or typed text:
 * - Lines and segments split on commas, semicolons, or newlines
 * - RFC-like "Display Name <email@domain>" (angle brackets) — only the email is kept
 * - Plain emails on each segment
 * - Also finds all bracketed emails first, then parses remainder for loose addresses
 */
export function parseRecipientTokens(text: string): string[] {
  const normalized = text.replace(/\r\n/g, '\n');
  if (!normalized.trim()) return [];

  const found: string[] = [];

  const angleRe = /<([^<>]+@[^<>]+)>/g;
  let angleMatch: RegExpExecArray | null;
  while ((angleMatch = angleRe.exec(normalized)) !== null) {
    found.push(angleMatch[1].trim());
  }

  let remainder = normalized;
  remainder = remainder.replace(/\s*<[^<>]*@[^<>]*>\s*/g, ' ');
  remainder = remainder.replace(/"[^"]*"/g, ' ');

  remainder
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((segment) => {
      const loose = segment.match(/[^\s,;<>]+@[^\s,;<>]+\.[^\s,;<>]{2,}/);
      if (loose) found.push(loose[0].trim());
    });

  return dedupeEmails(found);
}

/** Split stored comma-separated list into individual addresses (for chip display). */
export function splitEmailList(commaSeparated: string): string[] {
  if (!commaSeparated.trim()) return [];
  return commaSeparated
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function joinEmailList(emails: string[]): string {
  return dedupeEmails(emails).join(', ');
}

/** At least one syntactically valid email after parsing the stored string. */
export function hasValidRecipientEmails(commaSeparated: string): boolean {
  return splitEmailList(commaSeparated).some(isValidEmailAddress);
}

/** Only valid, normalized emails for API payloads. */
export function toBackendEmailList(commaSeparated: string): string {
  return splitEmailList(commaSeparated).filter(isValidEmailAddress).map(normalizeEmail).join(', ');
}
